import { ReactElement, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineArrowPath, HiOutlineDevicePhoneMobile } from 'react-icons/hi2';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import FormCard from '../../components/FormCard';
import Spinner from '../../components/Spinner';
import SessionConfigPicker from '../../components/SessionConfigPicker';
import { FileScanEpisode, FileScanRun, importEpisodeRun } from '../../api/episodeDbScan';
import { useEpisodeFileScan } from '../../hooks/useEpisodeDbScan';
import { useSessionsWithConfigs } from '../../hooks/useSessions';
import { apiErrorDetail } from '../../utils/apiError';
import { cn } from '../../utils/cn';
import { configShortLabel } from '../../utils/configRole';
import { EpisodeSessionLink, RunServerLink } from './EpisodeLinkControls';
import {
  baselineConfig,
  configOptionLabel,
  destinationSessions,
  SessionConfigChoice,
} from '../../utils/sessionConfigChoice';

/**
 * The runs one recording file lists, checked against the server as soon as the
 * file is opened. A run the server does not have — or has, in no session —
 * gets a tick box; the ticked runs go to the session and config letter chosen
 * underneath, one request per run.
 *
 * Each recording can also be linked to a session, and a run the server has
 * under no phone run id (a device zip) linked to the phone's run — see
 * EpisodeLinkControls. Neither builds anything.
 *
 * A file lists more than it holds: the phone copies its list of runs forward
 * into every new recording, and keeps each run's sensor data in the file it
 * was recorded in. So every episode says which file that is, and its runs are
 * imported from there — which is often not the file that is open.
 */

const CHECKBOX_STYLE = { accentColor: '#03365D', width: '18px', height: '18px' };

const chipClass = 'cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-semibold';
const smallButtonClass =
  'cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs cr-font-medium ' +
  'cr-text-gray-700 hover:cr-bg-gray-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-50';

function runKey(episode: FileScanEpisode, run: FileScanRun): string {
  return `${episode.episode_id}|${run.run_uuid}`;
}

/** A run of the caller's own with no home: not on the server, or on it in no session. */
function needsHome(episode: FileScanEpisode, run: FileScanRun): boolean {
  return episode.ownership === 'mine' && (!run.on_server || !run.config_id);
}

/** The server's verdict, which also wants the file holding the run's data to be readable. */
function canImport(episode: FileScanEpisode, run: FileScanRun): boolean {
  return run.importable && !!episode.data_file;
}

function runTimes(run: FileScanRun): string {
  if (run.start_ts_ms == null) return '';
  const start = new Date(run.start_ts_ms).toLocaleString();
  // No end: the phone never closed the run and nothing after it bounds it.
  if (run.end_ts_ms == null) return `${start} – not closed`;
  return `${start} – ${new Date(run.end_ts_ms).toLocaleTimeString()}`;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function summaryLine(runCount: number, placeableCount: number, strandedCount: number): string {
  if (runCount === 0) return 'No runs are listed in this file.';
  const listed = `${plural(runCount, 'run')} listed.`;
  const why = `the note beside ${strandedCount === 1 ? 'it says' : 'them says'} why.`;
  if (placeableCount === 0) {
    if (strandedCount === 0) return `${listed} None of them needs importing.`;
    const verb = strandedCount === 1 ? 'needs' : 'need';
    return `${listed} ${strandedCount} ${verb} a session but cannot be imported yet — ${why}`;
  }
  const verb = placeableCount === 1 ? 'needs' : 'need';
  const todo = `${placeableCount} still ${verb} a session — tick the ones to import and choose where they go.`;
  if (strandedCount === 0) return `${listed} ${todo}`;
  return `${listed} ${todo} ${strandedCount} more cannot be imported yet — ${why}`;
}

/** What a file that lists other recordings' runs needs said about it, or null when it lists only its own. */
function listingNote(episodes: FileScanEpisode[]): string | null {
  if (episodes.every((episode) => episode.data_file?.is_this_file)) return null;
  const rest = 'on the same phone, and each run is imported from the file it was recorded in.';
  if (episodes.some((episode) => episode.data_file?.is_this_file)) {
    return `This file also lists the runs of the recordings made before it ${rest}`;
  }
  return `No run was recorded in this file itself. It lists the runs of the recordings made before it ${rest}`;
}

interface RunRowProps {
  episode: FileScanEpisode;
  run: FileScanRun;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}

function RunRow({ episode, run, checked, disabled, onToggle }: RunRowProps): ReactElement {
  const selectable = canImport(episode, run);
  const placed = run.on_server && !!run.config_id;
  const runLink = run.session_id
    ? `/sessions/${run.session_id}/runs/${run.rundata_id}/overview`
    : `/runs/${run.rundata_id}/overview`;

  return (
    <label
      className={cn(
        'cr-flex cr-items-start cr-gap-2 cr-rounded-md cr-px-2 cr-py-1 cr-text-left cr-text-xs',
        selectable && 'cr-cursor-pointer hover:cr-bg-gray-50',
      )}
    >
      {/* The box keeps its column for runs that need nothing, so the labels line up. */}
      <span className="cr-flex cr-h-5 cr-w-5 cr-shrink-0 cr-items-center">
        {selectable && (
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onToggle}
            className="cr-cursor-pointer"
            style={CHECKBOX_STYLE}
          />
        )}
      </span>
      {/* Its own column, so a row too long for the card wraps under its label, not under the box. */}
      <span className="cr-flex cr-min-h-5 cr-min-w-0 cr-flex-wrap cr-items-center cr-gap-x-2 cr-gap-y-1">
        <span className="cr-font-medium cr-text-gray-700">
          {run.run_number != null ? `Run ${run.run_number} — ` : ''}
          {run.description || 'Run'}
        </span>
        <span className="cr-text-gray-400">{runTimes(run)}</span>
        {run.partial && (
          <span className="cr-text-amber-600" title="The end of this run was inferred, not recorded">
            partial
          </span>
        )}
        {placed && (
          <>
            <span className={cn(chipClass, 'cr-bg-green-100 cr-text-green-800')}>on server</span>
            {/* Whose run it is decides whether there is a page to open: another team's has none here. */}
            {run.rundata_id && (
              <Link to={runLink} className="cr-text-blue-700 hover:cr-underline">
                {run.session_description || 'Session'} / {configShortLabel(run.config_letter_number)}
              </Link>
            )}
          </>
        )}
        {run.on_server &&
          !run.config_id &&
          (episode.ownership === 'other_team' ? (
            <span className={cn(chipClass, 'cr-bg-green-100 cr-text-green-800')}>on server</span>
          ) : (
            <span
              className={cn(chipClass, 'cr-bg-amber-100 cr-text-amber-800')}
              title="The server has this run, but it belongs to no session yet"
            >
              on server · not in a session
            </span>
          ))}
        {!run.on_server && (
          <span
            className={cn(chipClass, 'cr-bg-red-100 cr-text-red-700')}
            title={
              run.deleted_on_server
                ? 'This run was on the server and has been deleted. Importing it brings it back as a new run.'
                : 'The server has no run for this part of the recording'
            }
          >
            {run.deleted_on_server ? 'deleted on server' : 'missing on server'}
          </span>
        )}
        {run.build_error && (
          <span className="cr-text-red-600" title={run.build_error}>
            build error
          </span>
        )}
      </span>
    </label>
  );
}

/** Why an episode an admin can see cannot be imported from, or null when it can. */
function ownershipNote(episode: FileScanEpisode): string | null {
  if (episode.ownership === 'other_team') {
    return `Belongs to ${episode.team_name || 'another team'} — switch to that team to import its runs`;
  }
  if (episode.ownership === 'unregistered') {
    return 'The phone never registered this recording — link it to a session to claim it for your team';
  }
  return null;
}

/** Why runs of the caller's own that need a session have no tick box, or null when none is left without one. */
function strandedNote(episode: FileScanEpisode): string | null {
  if (!episode.runs.some((run) => needsHome(episode, run) && !canImport(episode, run))) return null;
  const file = episode.data_file;
  if (!file) return 'This file does not say which recording holds this episode’s data, so its runs cannot be imported';
  if (file.is_this_file) return 'This file holds no sensor data for this episode, so its runs cannot be imported';
  return `${file.filename} is not on the server — upload that recording from the phone to import its runs`;
}

/** Where an episode's data is, for the line that heads its runs. */
function recordedIn(episode: FileScanEpisode): string | null {
  if (!episode.data_file) return null;
  return episode.data_file.is_this_file ? 'recorded in this file' : `recorded in ${episode.data_file.filename}`;
}

interface EpisodeRunsPanelProps {
  s3Key: string | undefined;
}

export default function EpisodeRunsPanel({ s3Key }: EpisodeRunsPanelProps): ReactElement {
  const queryClient = useQueryClient();
  const scan = useEpisodeFileScan(s3Key);
  const { data: sessions, isPending: sessionsLoading } = useSessionsWithConfigs();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Null until the user touches the picker, so the suggestion below can still
  // arrive after it — the sessions query and the scan land in either order.
  const [choice, setChoice] = useState<SessionConfigChoice | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  // A phone opens an episode every time the app starts, so a file is mostly
  // episodes in which nothing was recorded. Only the ones with runs are shown,
  // the latest first: that is the riding someone has just come to import.
  const allEpisodes = scan.data?.episodes ?? [];
  const episodes = useMemo(
    () =>
      allEpisodes
        .filter((episode) => episode.runs.length > 0)
        .sort((a, b) => (b.start_ts_ms ?? 0) - (a.start_ts_ms ?? 0)),
    [scan.data],
  );
  const emptyEpisodes = allEpisodes.length - episodes.length;

  // Each run goes with the file its data is in, which is where its import
  // is sent — the open file only for the episode that was recorded in it.
  const placeable = useMemo(
    () =>
      episodes.flatMap((episode) =>
        episode.runs
          .filter((run) => canImport(episode, run))
          .map((run) => ({ episode, run, key: runKey(episode, run), sourceKey: episode.data_file?.s3_key ?? '' })),
      ),
    [episodes],
  );
  const strandedCount = episodes.reduce(
    (sum, episode) => sum + episode.runs.filter((run) => needsHome(episode, run) && !canImport(episode, run)).length,
    0,
  );

  // Start on the session the phone was pointed at when it recorded the first
  // run that needs a home, if that is a session runs can go to. It is a
  // suggestion: nothing moves until Import is pressed, and the letter is
  // there to be changed.
  const suggested = useMemo<SessionConfigChoice>(() => {
    const sessionId = placeable.find((item) => item.episode.testsession_id)?.episode.testsession_id;
    const session = sessionId ? (destinationSessions(sessions).find((s: any) => s.id === sessionId) ?? null) : null;
    return { session, config: baselineConfig(session) };
  }, [placeable, sessions]);
  const destination = choice ?? suggested;
  // Read through `placeable`, so a tick left over from before a rescan cannot
  // send a run that no longer needs placing.
  const chosen = placeable.filter((item) => selected.has(item.key));
  const importing = progress !== null;

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const importChosen = async () => {
    const config = destination.config;
    if (!config || chosen.length === 0) return;
    const failed: { key: string; detail: string }[] = [];
    let placed = 0;
    setProgress({ done: 0, total: chosen.length });
    // One at a time. Each is a build on the server, the first also brings the
    // file into the server's store if it was never ingested, and a run that
    // fails should not take the ones after it down too.
    for (let i = 0; i < chosen.length; i += 1) {
      const { episode, run, key, sourceKey } = chosen[i];
      try {
        const result = await importEpisodeRun(sourceKey, episode.episode_id, run.run_uuid, config.id);
        if (result.outcome === 'created' || result.outcome === 'attached') placed += 1;
      } catch (err) {
        failed.push({ key, detail: apiErrorDetail(err) });
      }
      setProgress({ done: i + 1, total: chosen.length });
    }
    setProgress(null);
    // The runs that failed stay ticked, ready to be tried again.
    setSelected(new Set(failed.map((f) => f.key)));

    if (placed > 0) {
      toast.success(`${plural(placed, 'run')} imported into ${configOptionLabel(config)} — processing now`);
    } else if (failed.length === 0) {
      toast('Those runs were already on the server');
    }
    if (failed.length > 0) {
      toast.error(`${plural(failed.length, 'run')} could not be imported: ${failed[0].detail}`);
    }

    const sessionId = destination.session?.id;
    await Promise.all([
      scan.refetch(),
      queryClient.invalidateQueries({ queryKey: ['runs'] }),
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] }),
      queryClient.invalidateQueries({ queryKey: ['session-summary', sessionId] }),
    ]);
  };

  const runCount = episodes.reduce((sum, episode) => sum + episode.runs.length, 0);
  // A run with a server run starting beside it is most likely that run, arrived
  // as a zip: it is linked, not imported, so "Select all" leaves it out.
  const selectAllKeys = placeable.filter((item) => !item.run.link_candidates?.length).map((item) => item.key);
  const allChosen = selectAllKeys.length > 0 && chosen.length === selectAllKeys.length;
  const relink = () => scan.refetch();
  const listing = listingNote(episodes);

  return (
    <FormCard className="md:cr-w-[860px]">
      <div className="cr-flex cr-flex-col cr-gap-2 cr-p-3">
        <div className="cr-flex cr-items-center cr-justify-between cr-gap-2">
          <span className="cr-text-left cr-font-semibold">Runs listed in this recording</span>
          <button
            type="button"
            onClick={() => scan.refetch()}
            disabled={scan.isFetching || importing}
            title="Read the recording again and re-check its runs against the server"
            className={cn(smallButtonClass, 'cr-flex cr-items-center cr-gap-1')}
          >
            <HiOutlineArrowPath className="cr-h-4 cr-w-4" />
            {scan.isFetching ? 'Checking…' : 'Check again'}
          </button>
        </div>

        {scan.isPending && (
          <div className="cr-flex cr-items-center cr-gap-2 cr-text-sm cr-text-gray-600">
            <Spinner isFetching />
            <span>Reading the recording and checking its runs against the server…</span>
          </div>
        )}

        {scan.isError && (
          <p className="cr-text-left cr-text-sm cr-text-red-600">
            Could not check this recording: {apiErrorDetail(scan.error)}
          </p>
        )}

        {scan.data && (
          <>
            <p className="cr-text-left cr-text-xs cr-text-gray-500">
              {summaryLine(runCount, placeable.length, strandedCount)}
            </p>
            {runCount > 0 && listing && <p className="cr-text-left cr-text-xs cr-text-gray-500">{listing}</p>}

            {episodes.map((episode) => {
              const note = ownershipNote(episode) ?? strandedNote(episode);
              const where = recordedIn(episode);
              return (
                <div key={episode.episode_id} className="cr-border-t cr-border-gray-100 cr-pt-2">
                  <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-text-xs cr-font-semibold cr-text-gray-700">
                    <HiOutlineDevicePhoneMobile className="cr-h-4 cr-w-4" />
                    <span title={`Device ${episode.device_id || 'unknown'} — episode ${episode.episode_id}`}>
                      {episode.device_id ? episode.device_id.slice(-4) : '????'}
                    </span>
                    {episode.start_ts_ms != null && (
                      <span className="cr-font-normal cr-text-gray-500">
                        {new Date(episode.start_ts_ms).toLocaleString()}
                      </span>
                    )}
                    {where && <span className="cr-font-normal cr-text-gray-400">{where}</span>}
                    {note && <span className="cr-font-normal cr-text-amber-700">{note}</span>}
                  </div>
                  {episode.linkable && (
                    <EpisodeSessionLink episode={episode} sessions={sessions} disabled={importing} onLinked={relink} />
                  )}
                  <div className="cr-mt-1 cr-flex cr-flex-col">
                    {episode.runs.map((run) => (
                      <div key={run.run_uuid}>
                        <RunRow
                          episode={episode}
                          run={run}
                          checked={selected.has(runKey(episode, run))}
                          disabled={importing}
                          onToggle={() => toggle(runKey(episode, run))}
                        />
                        <RunServerLink episode={episode} run={run} disabled={importing} onLinked={relink} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {emptyEpisodes > 0 && (
              <p className="cr-border-t cr-border-gray-100 cr-pt-2 cr-text-left cr-text-xs cr-text-gray-400">
                {emptyEpisodes === 1
                  ? '1 episode in which no run was recorded is not shown.'
                  : `${emptyEpisodes} episodes in which no run was recorded are not shown.`}
              </p>
            )}

            {placeable.length > 0 && (
              <div className="cr-mt-2 cr-rounded-md cr-border cr-border-gray-200 cr-bg-gray-50 cr-py-2 md:cr-max-w-[600px]">
                <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-px-4 cr-pb-1">
                  <span className="cr-text-sm cr-font-semibold cr-text-gray-700">Import into</span>
                  <span className="cr-grow" />
                  <button
                    type="button"
                    className={smallButtonClass}
                    disabled={importing || (selectAllKeys.length === 0 && chosen.length === 0)}
                    onClick={() => setSelected(allChosen || chosen.length > 0 ? new Set() : new Set(selectAllKeys))}
                  >
                    {allChosen || chosen.length > 0 ? 'Clear selection' : `Select all (${selectAllKeys.length})`}
                  </button>
                </div>
                <SessionConfigPicker
                  sessions={sessions}
                  sessionsLoading={sessionsLoading}
                  value={destination}
                  onChange={setChoice}
                  disabled={importing}
                  action={
                    <button
                      type="button"
                      className="cr-rounded cr-border-none cr-bg-blue-900 cr-px-3 cr-py-1 cr-text-xs
                        cr-font-semibold cr-text-white cr-transition-colors hover:cr-bg-blue-850
                        disabled:cr-cursor-not-allowed disabled:cr-bg-cirrusBlue-100 disabled:cr-opacity-60"
                      disabled={!destination.config || chosen.length === 0 || importing}
                      onClick={importChosen}
                    >
                      {progress
                        ? `Importing ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`
                        : `Import ${plural(chosen.length, 'run')}`}
                    </button>
                  }
                />
                {!sessionsLoading && destinationSessions(sessions).length === 0 && (
                  <p className="cr-px-4 cr-text-left cr-text-xs cr-text-gray-500">
                    There is no session to import into yet — create one first.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </FormCard>
  );
}
